import Controller, { OnBlur, OnClick, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../BaseController';
import { Inject } from '@odinjs/odin';
import { FieldAPI, FormAPI, ImageAPI, InputAPI, PanelAPI, RecaptchaAPI } from '@philips-internal/oswald-ui-components';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { CONFIRMATION_FIELD_VALID, confirmationFieldValidator } from '../../utils/user-util';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import ConfigurationService from '../../services/configuration-service';
import { Value } from '@philips-internal/oswald-ui-core';
import { UsernameValidatorUtils } from '../../utils/username-validator-utils';
import { USERNAME_MIN_LENGTH_VALIDATION } from '../../constants/application-constants';
import { getCatpchaPublicKey } from '../../utils/captcha-utils';

interface FormResetPasswordProps extends Record<string, Value> {
  captchaResponse?: string;
  newPassword: string;
  newPasswordConfirmation: string;
  processCode?: string;
  username?: string;
}
interface ResetPasswordTokenResponseDto {
  accountCreatedByTasy: boolean;
  userSuggestionCreatedInTasy?: string;
}

enum ContextKeys {
  ORIGINAL_USERNAME_SUGGESTION = 'originalUsernameSuggestion',
}

@Controller({ component: 'root-layout', fragment: 'password-reset' })
class PasswordResetController extends BaseController {

  @Inject('form-password')
    formPasswordAPI!: FormAPI;

  @Inject('confirm-password-field')
    confirmPasswordFieldAPI!: FieldAPI;

  @Inject('field-username')
    fieldUsernameAPI!: FieldAPI;

  @Inject('panel-button')
    panelButtonForAPI!: PanelAPI;

  @Inject('field-recaptcha')
    recaptchaFieldAPI!: FieldAPI;

  @OnMount('form-password')
  handleConfirmPasswordLoading(): void {
    this.confirmPasswordFieldAPI.addLocalValidator(CONFIRMATION_FIELD_VALID, (value) => {
      const confirmationValue = this.formPasswordAPI.getRecord()['newPassword'];
      return confirmationFieldValidator(value, confirmationValue);
    });
  }

  @OnMount('image-logo')
  handleLogoImageLoading(event: MountEvent): void {
    const imageAPI = event.target as ImageAPI;
    imageAPI.setSrc('/static/assets/philips-shield.svg');
    ConfigurationService.getConfigurationData().then(layoutData => {
      imageAPI.setSrc(layoutData.dsLogoLogin);
    });
  }

  @OnBlur('field-username')
  handleFieldUsernameOnFocusOut(event: MountEvent): void {
    const fieldAPI = event.target as FieldAPI;
    const originalUsername = this.context.get(ContextKeys.ORIGINAL_USERNAME_SUGGESTION) as string;
    UsernameValidatorUtils.validateUsernameField(fieldAPI, originalUsername);
  }

  @OnMount('field-username')
  onLayoutMount(event: MountEvent): void {
    const fieldAPI = event.target as FieldAPI;
    const queryParams = this.historyService.getCurrentRouteQueryParams();
    if (queryParams && queryParams.user) {
      this.activationAccountRoute(fieldAPI, queryParams.user as string);
      return;
    }
    const token = this.historyService.getCurrentRouteQueryParams().token || '';
    HttpService
      .get<ResetPasswordTokenResponseDto | undefined>(APPLICATION_ENDPOINTS.GET_VALIDATE_RESET_PASSWORD_TOKEN, { token })
      .then(response => { this.addUsernameSuggestionAndValidate(fieldAPI, response.body) })
      .catch(() => this.historyService.push(APPLICATION_ROUTES.FORGET_PASSWORD_RESET_FAILURE));
  }

  private addUsernameSuggestionAndValidate = (fieldAPI: FieldAPI, result?: ResetPasswordTokenResponseDto): void => {
    if (!result?.accountCreatedByTasy) {
      return;
    }

    this.context.set({ [ContextKeys.ORIGINAL_USERNAME_SUGGESTION]: result.userSuggestionCreatedInTasy });
    setTimeout(() => {
      fieldAPI.setVisible(true);
      fieldAPI.addValidator({ name: 'required' });
      fieldAPI.setHelperText(MessageService.translate('patient.input.min-length', { minCaracter: USERNAME_MIN_LENGTH_VALIDATION }));
    })
  }

  private activationAccountRoute(fieldAPI: FieldAPI, user: string): void {
    this.addUsernameSuggestionAndValidate(fieldAPI, {
      accountCreatedByTasy: true,
      userSuggestionCreatedInTasy: user,
    }
    );
  }

  @OnMount('input-username')
  handleInputUsernameLoading(event: MountEvent): void {
    const userSuggestionCreatedInTasy = this.context.get(ContextKeys.ORIGINAL_USERNAME_SUGGESTION) as string;
    if (userSuggestionCreatedInTasy) {
      const inputAPI = event.target as InputAPI;
      inputAPI.setPlaceholder(`${MessageService.translate('tws.appointment-suggestion.suggestion')}: ${userSuggestionCreatedInTasy}`);
    }
  }

  @OnClick('button-confirm')
  onChangePasswordAdmitButton(): void {
    this.formPasswordAPI.submit();
  }

  @OnSubmit('form-password')
  handleFormPasswordSubmit(event: SubmitEvent): void {
    const body = event.record as FormResetPasswordProps;
    const queryParams = this.historyService.getCurrentRouteQueryParams();

    if (queryParams && queryParams.user) {
      body.processCode = 'AC';
    }

    const token = this.historyService.getCurrentRouteQueryParams().token || '';

    this.formPasswordAPI.setLoading(true);
    this.panelButtonForAPI.setVisible(false);

    HttpService.post(APPLICATION_ENDPOINTS.EMAILSENDER_SUCCESS_MAIL, { token }, { body }).then(() => {
      this.historyService.push(APPLICATION_ROUTES.FORGET_PASSWORD_RESET_SUCCESS);
    }).catch(() => {
      this.historyService.push(APPLICATION_ROUTES.FORGET_PASSWORD_RESET_FAILURE)
    });
  }

  @OnMount('recaptcha')
  handleRecaptchaLoading(event: MountEvent): void {
    const recaptchaAPI = event.target as RecaptchaAPI;
    getCatpchaPublicKey().then(async publicKey => {
      if (publicKey && publicKey != null && publicKey.length>0){
        recaptchaAPI.setSiteKey(publicKey);
      } else {
        this.recaptchaFieldAPI.setValidators([]);
        this.recaptchaFieldAPI.setVisible(false);
        await this.recaptchaFieldAPI.validate();
      }
    });
  }
}

export default PasswordResetController;
